// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { Modal, Platform, Pressable, Share, StyleSheet, View, useWindowDimensions } from 'react-native';

import GoffyButton from '@/components/GoffyButton';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getPublicTourPhotoUrl, getTourPhotoUri } from '@/services/tourPhotoSource';
import appColors, { alpha } from '@/theme/colors';

import { formatTakenAt } from '../formatTakenAt';
import { shareTourPhoto } from '../sharePhoto';
import type { TourPhoto } from '../types';
import AuthedImage from './AuthedImage';

type PhotoLightboxProps = {
  photo: TourPhoto | null;
  apiBaseUrl: string;
  token: string | null | undefined;
  onClose: () => void;
  onDelete?: (photo: TourPhoto) => Promise<void>;
  /** Promote this photo to the tour's cover image. Resolves false when the request was refused. */
  onSetAsTourImage?: (photo: TourPhoto) => Promise<boolean>;
  /**
   * Flip whether anyone with the link can see this photo. The caller owns the photo state, so it
   * must also refresh the `photo` prop for the toggle label to follow.
   */
  onSetVisibility?: (photo: TourPhoto, isPublic: boolean) => Promise<boolean>;
};

/**
 * Full-size view of one photo, with its caption, who took it, and where.
 *
 * Delete is available to any tour member, not just the uploader — the tour members payload carries
 * no organizer role, so an uploader-or-organizer rule cannot be expressed yet. That makes the
 * confirmation step load-bearing rather than decorative, and the uploader's name is shown right
 * above it so it is clear whose photo is about to go.
 */
export default function PhotoLightbox({
  photo,
  apiBaseUrl,
  token,
  onClose,
  onDelete,
  onSetAsTourImage,
  onSetVisibility,
}: PhotoLightboxProps) {
  const { t } = useI18n();
  const { width, height } = useWindowDimensions();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingCover, setSettingCover] = useState(false);
  const [coverNote, setCoverNote] = useState<string | null>(null);
  const [settingVisibility, setSettingVisibility] = useState(false);
  const [sharingPhoto, setSharingPhoto] = useState(false);
  const [shareNote, setShareNote] = useState<string | null>(null);

  const close = () => {
    setConfirmingDelete(false);
    setDeleting(false);
    setSettingCover(false);
    setCoverNote(null);
    setSettingVisibility(false);
    setSharingPhoto(false);
    setShareNote(null);
    onClose();
  };

  if (!photo) {
    return null;
  }

  // Subtract BOTH the backdrop padding and the card's own padding. Using only the backdrop's made
  // the image exactly as wide as the viewport, so the card around it overflowed by its own padding
  // and the action row was clipped at both edges on a phone.
  const imageWidth = Math.min(width - BACKDROP_PADDING * 2 - CARD_PADDING * 2, 900);
  const imageHeight = Math.min(height * 0.6, imageWidth);
  const takenAt = formatTakenAt(photo.createdAt);

  // Three buttons never fit across a phone. Wrapping alone left them right-aligned at their natural
  // width — a ragged half-width column with a dead gutter beside it. Below this width they stack
  // full-width instead, which is also easier to hit with a thumb.
  const stackActions = width < ACTIONS_STACK_BREAKPOINT;
  const actionStyle = stackActions ? styles.actionButtonStacked : undefined;

  const handleSetAsTourImage = async () => {
    if (!onSetAsTourImage || settingCover) {
      return;
    }
    setSettingCover(true);
    setCoverNote(null);
    try {
      const accepted = await onSetAsTourImage(photo);
      // Deliberately stays open with a note rather than closing: the new cover is not visible yet
      // (the resize has to run first), so closing would look like nothing happened.
      setCoverNote(accepted ? t('tourPhotos.setAsTourImageQueued') : t('tourPhotos.setAsTourImageFailed'));
    } finally {
      setSettingCover(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) {
      return;
    }
    setDeleting(true);
    try {
      await onDelete(photo);
      close();
    } finally {
      setDeleting(false);
    }
  };

  const handleSetVisibility = async () => {
    if (!onSetVisibility || settingVisibility) {
      return;
    }
    setSettingVisibility(true);
    setShareNote(null);
    try {
      const accepted = await onSetVisibility(photo, !photo.isPublic);
      if (!accepted) {
        setShareNote(t('tourPhotos.visibilityFailed'));
      }
    } finally {
      setSettingVisibility(false);
    }
  };

  /**
   * Sends the photo itself — the file, not a link — to WhatsApp, Messenger, Instagram or whatever
   * else the device offers. Unlike the link share below this works on a private photo, because the
   * bytes travel with the viewer's own token and never become publicly reachable.
   */
  const handleSharePhoto = async () => {
    if (sharingPhoto) {
      return;
    }
    setSharingPhoto(true);
    // The bytes have to be downloaded before the share sheet can open, which is seconds on a
    // course's mobile connection. Without a note the button looks dead for that whole time.
    setShareNote(t('tourPhotos.sharePhotoPreparing'));
    try {
      const outcome = await shareTourPhoto({
        photo,
        apiBaseUrl,
        token,
        title: t('tourPhotos.sharePhoto'),
      });
      if (outcome === 'downloaded') {
        setShareNote(t('shareImage.downloaded'));
      } else if (outcome === 'unsupported') {
        setShareNote(t('shareImage.unsupported'));
      } else {
        // Shared or dismissed — either way the sheet has said what happened.
        setShareNote(null);
      }
    } catch {
      setShareNote(t('tourPhotos.sharePhotoFailed'));
    } finally {
      setSharingPhoto(false);
    }
  };

  const handleShare = async () => {
    const url = getPublicTourPhotoUrl(apiBaseUrl, photo.tourId, photo.id);
    if (!url) {
      return;
    }
    try {
      if (Platform.OS === 'web') {
        // The Web Share API is mobile-browser territory; on desktop the clipboard is the share
        // sheet, so say what happened instead of failing silently.
        const nav = navigator as Navigator & { share?: (data: { url: string }) => Promise<void> };
        if (nav.share) {
          await nav.share({ url });
        } else {
          await navigator.clipboard.writeText(url);
          setShareNote(t('tourPhotos.shareCopied'));
        }
      } else {
        // Both fields on purpose: iOS shares `url`, Android shares `message`.
        await Share.share({ message: url, url });
      }
    } catch (cause) {
      // An aborted share sheet also rejects; only surface real failures.
      if (cause instanceof Error && cause.name === 'AbortError') {
        return;
      }
      setShareNote(t('tourPhotos.shareFailed'));
    }
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      <Pressable style={styles.backdrop} onPress={close} testID="tour-photo-lightbox-backdrop">
        {/* Stops a tap inside the card from bubbling to the backdrop and closing the modal. */}
        <Pressable style={styles.card} onPress={(event) => event.stopPropagation()} testID="tour-photo-lightbox">
          <AuthedImage
            uri={getTourPhotoUri(apiBaseUrl, photo.tourId, photo.id, 'main', photo.hasMain)}
            token={token}
            style={{ width: imageWidth, height: imageHeight, borderRadius: 8 }}
            resizeMode="contain"
            testID="tour-photo-lightbox-image"
            // Show the thumbnail the grid already fetched while the full-size version loads. On web
            // the bytes have to be downloaded whole before anything can be painted (the byte route
            // needs an Authorization header, so a progressively-rendering <img src> is not an
            // option), which otherwise means staring at an empty box on a slow connection.
            placeholder={
              <AuthedImage
                uri={getTourPhotoUri(apiBaseUrl, photo.tourId, photo.id, 'thumb', photo.hasThumb)}
                token={token}
                style={{ width: imageWidth, height: imageHeight }}
                resizeMode="contain"
                testID="tour-photo-lightbox-preview"
              />
            }
          />

          {/* Who took it leads, then when, then the caption. Attribution is what people look for
              first, and a caption is optional — leading with it meant the name moved around
              depending on whether a caption happened to be set. */}
          <View style={styles.meta}>
            <Text style={styles.byline} testID="tour-photo-byline">
              {photo.uploadedByName ?? t('tourPhotos.unknownUploader')}
              {photo.holeNumber !== null ? ` · ${t('tourPhotos.holeLabel', { hole: photo.holeNumber })}` : ''}
            </Text>
            {takenAt ? (
              <Text style={styles.takenAt} testID="tour-photo-taken-at">
                {takenAt}
              </Text>
            ) : null}
            {photo.caption ? <Text style={styles.caption}>{photo.caption}</Text> : null}
            {photo.isPublic ? (
              <Text style={styles.publicNote} testID="tour-photo-public-note">
                {t('tourPhotos.publicNote')}
              </Text>
            ) : null}
            {coverNote ? (
              <Text style={styles.coverNote} testID="tour-photo-cover-note">
                {coverNote}
              </Text>
            ) : null}
            {shareNote ? (
              <Text style={styles.coverNote} testID="tour-photo-share-note">
                {shareNote}
              </Text>
            ) : null}
          </View>

          {confirmingDelete ? (
            <View style={styles.confirmRow}>
              <Text style={styles.confirmText}>{t('tourPhotos.deleteConfirm')}</Text>
              <View style={[styles.actions, stackActions && styles.actionsStacked]}>
                <GoffyButton
                  style={actionStyle}
                  label={t('tourPhotos.deleteConfirmYes')}
                  onPress={() => void handleDelete()}
                  disabled={deleting}
                  size="compact"
                  testID="tour-photo-delete-confirm"
                />
                <GoffyButton
                  style={actionStyle}
                  label={t('tourPhotos.cancel')}
                  onPress={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  size="compact"
                  tone="dark"
                  testID="tour-photo-delete-cancel"
                />
              </View>
            </View>
          ) : (
            <View style={[styles.actions, stackActions && styles.actionsStacked]}>
              <GoffyButton
                style={actionStyle}
                label={t('tourPhotos.sharePhoto')}
                onPress={() => void handleSharePhoto()}
                disabled={sharingPhoto}
                size="compact"
                tone="dark"
                testID="tour-photo-share-file"
              />
              {onSetVisibility ? (
                <GoffyButton
                  style={actionStyle}
                  label={photo.isPublic ? t('tourPhotos.makePrivate') : t('tourPhotos.makePublic')}
                  onPress={() => void handleSetVisibility()}
                  disabled={settingVisibility}
                  size="compact"
                  tone="dark"
                  testID="tour-photo-toggle-visibility"
                />
              ) : null}
              {photo.isPublic ? (
                <GoffyButton
                  style={actionStyle}
                  label={t('tourPhotos.shareAction')}
                  onPress={() => void handleShare()}
                  size="compact"
                  tone="dark"
                  testID="tour-photo-share"
                />
              ) : null}
              {onSetAsTourImage ? (
                <GoffyButton
                  style={actionStyle}
                  label={t('tourPhotos.setAsTourImage')}
                  onPress={() => void handleSetAsTourImage()}
                  disabled={settingCover}
                  size="compact"
                  tone="dark"
                  testID="tour-photo-set-cover"
                />
              ) : null}
              {onDelete ? (
                <GoffyButton
                  style={actionStyle}
                  label={t('tourPhotos.delete')}
                  onPress={() => setConfirmingDelete(true)}
                  size="compact"
                  tone="dark"
                  testID="tour-photo-delete"
                />
              ) : null}
              <GoffyButton
                style={actionStyle}
                label={t('tourPhotos.close')}
                onPress={close}
                size="compact"
                tone="accent"
                testID="tour-photo-lightbox-close"
              />
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const ACTIONS_STACK_BREAKPOINT = 480;
const BACKDROP_PADDING = 16;
const CARD_PADDING = 12;

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.background.app, 0.88),
    alignItems: 'center',
    justifyContent: 'center',
    padding: BACKDROP_PADDING,
  },
  card: {
    // Never wider than the screen, whatever the buttons inside want to be.
    maxWidth: '100%',
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: CARD_PADDING,
    gap: 12,
    alignItems: 'center',
  },
  meta: {
    alignSelf: 'stretch',
    gap: 4,
  },
  caption: {
    color: appColors.text.secondary,
    fontSize: 15,
  },
  byline: {
    // Now the lead line, so it gets the readable colour rather than the muted one it had when it
    // sat under the caption.
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  takenAt: {
    color: appColors.text.muted,
    fontSize: 12,
  },
  coverNote: {
    color: appColors.accent.primary,
    fontSize: 13,
  },
  publicNote: {
    color: appColors.accent.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  confirmRow: {
    alignSelf: 'stretch',
    gap: 8,
  },
  confirmText: {
    color: appColors.text.secondary,
    fontSize: 14,
  },
  actions: {
    flexDirection: 'row',
    // Three buttons do not fit across a phone, and without wrapping the row simply grew past the
    // screen edge — clipping the first and last labels rather than reflowing. Stretch so the row is
    // bounded by the card instead of by its own content width.
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    justifyContent: 'flex-end',
    gap: 8,
  },
  actionsStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  actionButtonStacked: {
    // GoffyButton's size styles set an explicit width (190 for compact), which alignSelf cannot
    // beat — so override the width itself. The component applies `style` after its size style, so
    // this wins.
    width: '100%',
    alignSelf: 'stretch',
  },
});
